import { Modal, Stack, Typography, useMediaQuery } from '@mui/material'
import { useTranslation } from 'next-i18next'
import { useContext, useEffect, useMemo, useState } from 'react'
import XMarkIcon from 'assets/icons/nucleo/18px_xmark.svg'
import {
  CurrencyContext,
  SetCurrencyContext
} from 'components/Hook/CurrencyContext'
import { Button, ButtonVariant } from 'components/common/Button'
import { LocaleContext } from 'components/common/LocaleContext'
import { Select } from 'components/common/Select/SelectV2'
import { FiltersBox } from 'containers/search/Filters/Filters.style'
import { usePriceFormat } from 'hooks/usePriceFormat'
import { unifiedColors } from 'styles/colors'
import { themeUtils } from 'styles/theme'
import { Drawer } from './Header.style'

interface SettingsModalProps {
  allCurrencies: string[]
  isSettingsOpen: boolean
  closeSettings: () => void
  setLanguageChange: (value: boolean) => void
}

export const SettingsModal = ({
  allCurrencies,
  isSettingsOpen,
  closeSettings,
  setLanguageChange
}: SettingsModalProps) => {
  const isMobile = useMediaQuery(themeUtils.maxWidth.md)

  const { saveLocale } = useContext(LocaleContext)
  const setCurrency = useContext(SetCurrencyContext)
  const currency = useContext(CurrencyContext)
  const { formatSymbol } = usePriceFormat()
  const { t, i18n } = useTranslation('common')

  useEffect(() => setLanguageChange(false), [i18n.language, currency])

  const languages = useMemo(() => {
    return [
      { value: 'fr', label: 'Français (FR)' },
      { value: 'en', label: 'English (EN)' },
      { value: 'es', label: 'Español (ES)' },
      { value: 'nl', label: 'Nederlands (NL)' }
    ]
  }, [])
  const currencies = useMemo(() => {
    return allCurrencies.map((currencyCode) => ({
      value: currencyCode,
      label: `${formatSymbol(currencyCode)} (${currencyCode.toUpperCase()})`
    }))
  }, [allCurrencies, formatSymbol])

  const selectedLanguage = languages.find(
    (lang) => lang.value === i18n.language
  )
  const selectedCurrency = currencies.find((curr) => curr.value === currency)

  const [settingsLanguage, setSettingsLanguage] = useState(selectedLanguage)
  const [settingsCurrency, setSettingsCurrency] = useState(selectedCurrency)

  const handleLanguageChange = (newLanguage: string) => {
    setSettingsLanguage(languages.find((lang) => lang.value === newLanguage))
  }
  const handleCurrencyChange = (newCurrency: string) => {
    setSettingsCurrency(currencies.find((curr) => curr.value === newCurrency))
  }

  const handleSave = () => {
    if (
      settingsLanguage?.value !== i18n.language ||
      settingsCurrency?.value !== currency
    ) {
      setLanguageChange(true)
    }
    settingsLanguage?.value && saveLocale(settingsLanguage.value)
    settingsCurrency?.value && setCurrency(settingsCurrency.value)
    closeSettings()
  }

  const handleClose = () => {
    setSettingsCurrency(selectedCurrency)
    setSettingsLanguage(selectedLanguage)
    closeSettings()
  }
  const MainComponent = isMobile ? Drawer : Modal
  return (
    <MainComponent open={isSettingsOpen} onClose={handleClose}>
      <FiltersBox>
        <Stack p={3} gap={3}>
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5" gutterBottom alignSelf="center" m="auto">
              {t('settings.title')}
            </Typography>
            <XMarkIcon
              style={{ transform: 'scale(1.225)', cursor: 'pointer' }}
              onClick={handleClose}
            />
          </Stack>
          <Stack gap={2}>
            <Select
              fullWidth
              title={t('settings.language')}
              label={settingsLanguage?.label ?? ''}
              options={languages}
              value={i18n.language}
              onSave={handleLanguageChange}
              saveOnChange
            />
            <Typography
              variant="body2"
              fontWeight={700}
              color={unifiedColors.label.subdued}
              alignSelf="center"
            >
              {t('settings.and')}
            </Typography>
            <Select
              fullWidth
              title={t('settings.currency')}
              label={settingsCurrency?.label ?? ''}
              options={currencies}
              value={currency}
              onSave={handleCurrencyChange}
              saveOnChange
            />
            <Button
              label={t('settings.save')}
              variant={ButtonVariant.Primary}
              onClick={handleSave}
              fullWidth
            />
          </Stack>
        </Stack>
      </FiltersBox>
    </MainComponent>
  )
}
